/**
 * LoaGenerateDialog
 * -----------------
 * Generate a customer-personalised Letter of Authority. Asks for:
 *   - DNO name (e.g. GTC, UKPN, Western Power Distribution, SSEN, Northern Powergrid)
 *   - Application type (G99 default, G98 alternative)
 *
 * On submit, calls POST /api/customers/{id}/loa/generate which:
 *   - Renders the LoA PDF with Helios header/footer + customer details
 *   - Attaches it to the customer's documents[] with doc_tag='letter_of_authority'
 *   - Returns the doc record
 *
 * Once generated, the admin can send the document for signing using the
 * existing per-document "Send for Signing" flow from the Documents tab.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { FilePen, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// UK DNOs — covers the 6 GB Distribution Network Operators + GTC
const COMMON_DNOS = [
  'GTC',
  'UK Power Networks (UKPN)',
  'National Grid Electricity Distribution',
  'Northern Powergrid',
  'SP Energy Networks',
  'SSEN (Scottish & Southern Electricity Networks)',
  'Electricity North West',
  'NIE Networks',
];

export const LoaGenerateDialog = ({ open, onOpenChange, customer, onGenerated }) => {
  const [dnoName, setDnoName] = useState('');
  const [customDno, setCustomDno] = useState('');
  const [applicationType, setApplicationType] = useState('G99');
  const [sendForSigning, setSendForSigning] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setDnoName('');
      setCustomDno('');
      setApplicationType('G99');
      setSendForSigning(true);
    }
  }, [open]);

  const effectiveDno = dnoName === '__custom__' ? customDno.trim() : dnoName;

  const handleSubmit = async () => {
    if (!effectiveDno) {
      toast.error('Please select or enter a DNO');
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/customers/${customer.id}/loa/generate`, {
        dno_name: effectiveDno,
        application_type: applicationType,
        send_for_signing: sendForSigning,
      });
      if (sendForSigning) {
        if (r.data.sign_link?.email_sent) {
          toast.success(`LoA generated and sign link emailed to ${customer.email}`);
        } else {
          toast.success('LoA generated. Sign link created — check the Documents tab to copy/email manually.');
        }
      } else {
        toast.success('Letter of Authority generated — find it in the Documents tab to send for signing');
      }
      if (onGenerated) onGenerated();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate Letter of Authority');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="loa-generate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FilePen className="w-5 h-5 text-slate-700" />
            Generate Letter of Authority
          </DialogTitle>
          <DialogDescription>
            Creates a Helios-branded LoA authorising us to act on the customer's behalf in the DNO application. The doc will be attached to the customer file and can be sent for signing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-1.5 block">
              Customer
            </Label>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid="loa-customer-summary">
              <div className="font-medium text-slate-900">
                {customer?.first_name} {customer?.last_name}
              </div>
              <div className="text-xs text-slate-500">
                {customer?.address || '(no address on file)'}{customer?.postcode ? `, ${customer.postcode}` : ''}
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="loa-dno" className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-1.5 block">
              DNO
            </Label>
            <Select value={dnoName} onValueChange={setDnoName}>
              <SelectTrigger id="loa-dno" data-testid="loa-dno-select">
                <SelectValue placeholder="Select DNO" />
              </SelectTrigger>
              <SelectContent>
                {COMMON_DNOS.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
                <SelectItem value="__custom__">Other (type below)…</SelectItem>
              </SelectContent>
            </Select>
            {dnoName === '__custom__' && (
              <Input
                value={customDno}
                onChange={(e) => setCustomDno(e.target.value)}
                placeholder="e.g. Independent Operator Ltd"
                className="mt-2"
                data-testid="loa-custom-dno-input"
              />
            )}
          </div>

          <div>
            <Label htmlFor="loa-apptype" className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-1.5 block">
              Application type
            </Label>
            <Select value={applicationType} onValueChange={setApplicationType}>
              <SelectTrigger id="loa-apptype" data-testid="loa-apptype-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="G99">G99 (≥ 16 A per phase / battery)</SelectItem>
                <SelectItem value="G98">G98 (≤ 16 A per phase, micro-generation)</SelectItem>
                <SelectItem value="ENA Notification">ENA Notification</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <Checkbox
                checked={sendForSigning}
                onCheckedChange={(v) => setSendForSigning(!!v)}
                data-testid="loa-send-for-signing-checkbox"
                className="mt-0.5"
              />
              <div className="flex-1 text-sm">
                <div className="font-medium text-blue-900">Send for signing immediately</div>
                <div className="text-xs text-blue-700 mt-0.5">
                  Mints a public signing link and emails it to <span className="font-mono">{customer?.email || '(no email on file)'}</span>{' '}
                  using the "Letter of Authority — Sign Link" template (editable in Settings).
                </div>
              </div>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !effectiveDno}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="loa-generate-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            {sendForSigning ? 'Generate & Send for Signing' : 'Generate LoA'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LoaGenerateDialog;
